import {
  ApiError,
  type AuditEngagementFilters,
  type AuditEngagementPage,
  type AuditEventsFilters,
  type AuditEventsPage,
  type AuditSummary,
} from './client';

export interface AdminMe {
  id: string;
  email: string;
  displayName: string | null;
}

export interface CustomerSummary {
  id: string;
  slug: string;
  displayName: string;
  status: string;
  authMethod: 'oidc' | 'email_code';
  /**
   * When true, users of this customer can pick any other active customer
   * after sign-in and operate inside it.
   */
  isGlobal: boolean;
  createdAt: string;
  domainCount: number;
  oidcConfigured: boolean;
  gitlabConfigured: boolean;
  gitlabMode: 'repo' | 'pages' | null;
}

export interface CustomerDetail {
  id: string;
  slug: string;
  displayName: string;
  status: string;
  authMethod: 'oidc' | 'email_code';
  /**
   * When true, every PortalUser for this customer is implicitly granted
   * the reviewer role regardless of their per-user `canReviewDocuments`
   * flag. Toggled from the Customer edit page.
   */
  allPortalUsersCanReview: boolean;
  /**
   * When true, users of this customer can pick any other active customer
   * after sign-in and operate inside it (with per-customer admin grants
   * gating dashboard/reviewer rights on the visited tenant). Toggled from
   * the Customer edit page.
   */
  isGlobal: boolean;
  createdAt: string;
  emailDomains: string[];
  oidc: {
    issuerUri: string;
    clientId: string;
    clientSecretSet: boolean;
    scopes: string[];
    buttonLabel: string | null;
  } | null;
  email: {
    provider: 'smtp' | 'graph';
    fromAddress: string;
    fromName: string | null;
    subjectTemplate: string | null;
    headingTemplate: string | null;
    bodyFormat: 'html' | 'text';
    smtpHost: string | null;
    smtpPort: number;
    smtpSecure: boolean;
    smtpUser: string | null;
    smtpPasswordSet: boolean;
    graphTenantId: string | null;
    graphClientId: string | null;
    graphClientSecretSet: boolean;
  } | null;
  gitlab: {
    mode: 'repo' | 'pages';
    baseUrl: string;
    projectId: string | null;
    defaultBranch: string;
    accessTokenSet: boolean;
    contentRoot: string;
    pagesUrl: string | null;
    pagesJobName: string;
    includePatterns: string[];
    excludePatterns: string[];
    pdfAllowedPatterns: string[];
    pdfBlockedPatterns: string[];
    /**
     * Glob patterns marking files as excluded from the document review
     * workflow. Files matching any pattern hide the tree status dot,
     * `DocumentStatusControl`, and `ReviewersPopover` in the portal.
     */
    reviewExcludedPatterns: string[];
  } | null;
}

async function json<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    let message = text || res.statusText;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.message === 'string') message = parsed.message;
      else if (Array.isArray(parsed?.message)) message = parsed.message.join(', ');
    } catch {
      // not json — keep raw text
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface CacheState {
  status: 'empty' | 'fetching' | 'ok' | 'error';
  lastRefreshedAt: string | null;
  lastRefreshError: string | null;
  lastRefreshRef: string | null;
  fileCount: number | null;
  sizeBytes: number | null;
  scheduleIntervalSec: number | null;
  nextRefreshAt: string | null;
  ageSeconds: number | null;
}

export const adminApi = {
  login: (email: string, password: string) =>
    json<AdminMe>('/api/v1/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => json<void>('/api/v1/admin/logout', { method: 'POST' }),
  me: () => json<AdminMe>('/api/v1/admin/me'),

  listCustomers: () => json<CustomerSummary[]>('/api/v1/admin/customers'),
  getCustomer: (id: string) => json<CustomerDetail>(`/api/v1/admin/customers/${id}`),
  createCustomer: (payload: { slug: string; displayName: string }) =>
    json<CustomerDetail>('/api/v1/admin/customers', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCustomer: (
    id: string,
    payload: Partial<{
      slug: string;
      displayName: string;
      status: 'active' | 'suspended';
      authMethod: 'oidc' | 'email_code';
      allPortalUsersCanReview: boolean;
      isGlobal: boolean;
    }>,
  ) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteCustomer: (id: string) =>
    json<void>(`/api/v1/admin/customers/${id}`, { method: 'DELETE' }),

  replaceDomains: (id: string, domains: string[]) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/email-domains`, {
      method: 'PUT',
      body: JSON.stringify({ domains }),
    }),

  setOidc: (
    id: string,
    payload: {
      issuerUri: string;
      clientId: string;
      clientSecret?: string;
      scopes?: string[];
      buttonLabel?: string;
    },
  ) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/oidc`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  clearOidc: (id: string) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/oidc`, { method: 'DELETE' }),

  setEmailAuth: (
    id: string,
    payload: {
      provider: 'smtp' | 'graph';
      fromAddress: string;
      fromName?: string;
      subjectTemplate?: string;
      headingTemplate?: string;
      bodyFormat?: 'html' | 'text';
      smtpHost?: string;
      smtpPort?: number;
      smtpSecure?: boolean;
      smtpUser?: string;
      smtpPassword?: string;
      graphTenantId?: string;
      graphClientId?: string;
      graphClientSecret?: string;
    },
  ) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/email-auth`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  clearEmailAuth: (id: string) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/email-auth`, { method: 'DELETE' }),

  setGitlab: (
    id: string,
    payload: {
      mode: 'repo' | 'pages';
      baseUrl?: string;
      projectId?: string;
      defaultBranch?: string;
      accessToken?: string;
      contentRoot?: string;
      pagesUrl?: string;
      pagesJobName?: string;
      includePatterns?: string[];
      excludePatterns?: string[];
      pdfAllowedPatterns?: string[];
      pdfBlockedPatterns?: string[];
      reviewExcludedPatterns?: string[];
    },
  ) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/gitlab`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  clearGitlab: (id: string) =>
    json<CustomerDetail>(`/api/v1/admin/customers/${id}/gitlab`, { method: 'DELETE' }),

  cacheStatus: (id: string) =>
    json<CacheState>(`/api/v1/admin/customers/${id}/content/status`),
  refreshCache: (id: string) =>
    json<CacheState>(`/api/v1/admin/customers/${id}/content/refresh`, { method: 'POST' }),
  setCacheSchedule: (id: string, intervalMinutes: number | null) =>
    json<CacheState>(`/api/v1/admin/customers/${id}/content/schedule`, {
      method: 'PUT',
      body: JSON.stringify({ intervalMinutes }),
    }),

  listPortalUsers: (customerId: string) =>
    json<PortalUserSummary[]>(`/api/v1/admin/customers/${customerId}/portal-users`),
  updatePortalUser: (
    customerId: string,
    userId: string,
    patch: Partial<{ canViewAuditDashboard: boolean; canReviewDocuments: boolean }>,
  ) =>
    json<PortalUserSummary>(
      `/api/v1/admin/customers/${customerId}/portal-users/${userId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
      },
    ),

  auditSummary: (customerId: string, from?: string, to?: string) =>
    json<AuditSummary>(
      `/api/v1/admin/customers/${customerId}/audit/summary?${adminAuditQuery({ from, to })}`,
    ),
  auditEvents: (customerId: string, filters: AuditEventsFilters) =>
    json<AuditEventsPage>(
      `/api/v1/admin/customers/${customerId}/audit/events?${adminAuditQuery(
        { ...filters } as Record<string, string | number | undefined>,
      )}`,
    ),
  auditEngagements: (customerId: string, filters: AuditEngagementFilters) =>
    json<AuditEngagementPage>(
      `/api/v1/admin/customers/${customerId}/audit/engagements?${adminAuditQuery(
        { ...filters } as Record<string, string | number | undefined>,
      )}`,
    ),
};

function adminAuditQuery(
  params: Record<string, string | number | undefined>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === null) continue;
    qs.set(key, String(value));
  }
  return qs.toString();
}

export interface PortalUserSummary {
  id: string;
  email: string;
  displayName: string | null;
  canViewAuditDashboard: boolean;
  canReviewDocuments: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /**
   * Tenant the user originally signed in from. When `homeCustomer.id`
   * differs from the URL-param customer this row represents a global
   * user impersonating the visited tenant.
   */
  homeCustomer: {
    id: string;
    slug: string;
    displayName: string;
    isGlobal: boolean;
  };
}
